'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useTenantAndVariant } from '@multitenancy/TenantAndVariantContext';

export function HeroShowcase() {
  const { featureToggles, storeConfig } = useTenantAndVariant();
  const heroImage = storeConfig.heroImage || '/images/hero.png';
  const isFocused = featureToggles.heroWidthMode === 'focused';
  const isLimited = featureToggles.heroWidthMode === 'limited' || isFocused;

  return (
    <div className={`relative w-full ${isFocused ? 'bg-gradient-to-br from-[#8B3A3A] via-[#A04848] to-[#D4B896]' : ''}`}>
      {/* Blurred background for focused mode */}
      {isFocused && (
        <div className="absolute inset-0 overflow-hidden">
          <Image
            src={heroImage}
            alt="Background"
            fill
            className="object-cover blur-2xl scale-110 opacity-60"
            priority
          />
        </div>
      )}

      <section className={`relative h-screen w-full flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#8B3A3A] via-[#A04848] to-[#D4B896] ${isLimited ? 'max-w-[1920px] mx-auto' : ''}`}>
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:30px_30px]" />
        </div>

        {/* Split Layout - Image and Content */}
        <div className="relative z-10 w-full h-full flex flex-col md:flex-row">
          {/* Left Side - Image */}
          <div className="relative w-full md:w-1/2 h-1/2 md:h-full overflow-hidden">
            <Image
              src={heroImage}
              alt={storeConfig.displayName}
              fill
              className="object-cover scale-110 hover:scale-100 transition-transform duration-[3000ms] ease-out"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#8B3A3A]/20 to-[#8B3A3A]/60" />

            {/* Floating ornamental elements */}
            <div className="absolute top-10 left-10 w-20 h-20 border-2 border-[#D4B896]/60 rotate-45 animate-pulse" />
            <div className="absolute bottom-20 right-16 w-16 h-16 border-2 border-[#D4B896]/50 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
          </div>

          {/* Right Side - Content */}
          <div className="relative w-full md:w-1/2 h-1/2 md:h-full flex flex-col items-center justify-center px-6 md:px-12 lg:px-20">
            {/* Decorative top element */}
            <div className="mb-8 md:mb-12 animate-fade-in-down">
              <div className="flex items-center gap-3">
                <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#D4B896]/60" />
                <div className="w-3 h-3 bg-[#D4B896]/80 rotate-45" />
                <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#D4B896]/60" />
              </div>
            </div>

            {/* Main Heading - Modern Stacked */}
            <div className="text-center mb-8 md:mb-12 animate-fade-in-up">
              <h1 className="text-5xl sm:text-6xl md:text-6xl lg:text-8xl font-bold mb-4 text-white" style={{
                textShadow: '0 4px 30px rgba(0,0,0,0.5)',
                letterSpacing: '0.15em'
              }}>
                {storeConfig.displayName.split(' ')[0].toUpperCase()}
              </h1>
              <div className="w-24 h-1 bg-[#D4B896] mx-auto mb-4" />
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-[#D4B896]" style={{
                textShadow: '0 2px 20px rgba(0,0,0,0.5)',
                letterSpacing: '0.25em'
              }}>
                {storeConfig.displayName.split(' ').slice(1).join(' ').toUpperCase()}
              </h2>
            </div>

            {/* Subtitle */}
            <p className="text-sm md:text-base lg:text-lg font-light text-white/90 mb-10 md:mb-16 text-center max-w-md animate-fade-in-up-delayed" style={{
              textShadow: '0 2px 15px rgba(0,0,0,0.7)',
              letterSpacing: '0.2em',
              lineHeight: '2'
            }}>
              TIMELESS ELEGANCE<br />
              MEETS MODERN STYLE
            </p>

            {/* CTA Buttons - Dual Options */}
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up-more-delayed">
              <Link
                href="/store"
                className="group relative overflow-hidden"
              >
                <span className="relative z-10 inline-flex items-center gap-3 bg-[#D4B896] text-black px-10 py-4 text-xs font-bold tracking-widest uppercase transition-all duration-500 hover:bg-[#F5E6D3] hover:scale-105 hover:shadow-[0_0_30px_rgba(212,184,150,0.5)]">
                  SHOP NOW
                  <span className="text-sm">→</span>
                </span>
              </Link>
              <Link
                href="/store"
                className="group relative overflow-hidden"
              >
                <span className="relative z-10 inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border-2 border-white/60 text-white px-10 py-4 text-xs font-bold tracking-widest uppercase transition-all duration-500 hover:bg-white hover:text-black hover:border-white hover:scale-105">
                  NEW ARRIVALS
                </span>
              </Link>
            </div>

            {/* Decorative bottom element */}
            <div className="mt-12 md:mt-16 animate-fade-in-final">
              <div className="flex items-center gap-2">
                <div className="w-8 h-px bg-white/40" />
                <div className="w-2 h-2 rounded-full bg-[#D4B896]/60 animate-pulse" />
                <div className="w-8 h-px bg-white/40" />
              </div>
            </div>
          </div>
        </div>

        {/* Floating decorative circles */}
        <div className="absolute top-1/4 left-1/4 w-40 h-40 bg-[#D4B896]/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/3 right-1/3 w-60 h-60 bg-[#8B3A3A]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </section>
    </div>
  );
}
